class ProductGrouping extends HTMLElement {
  constructor() {
    super();
    this.debug = false;
    this.parentComponent = this;
    this.sectionId = this.getAttribute("data-section-id");
    this.optionsToRequestData = this.querySelectorAll(
      "[js-request-new-product]"
    );
    this.buttonsToUpdateSubOptions = this.querySelectorAll(
      "[js-update-sub-option]"
    );
    this.buttonsToUpdateVariant = this.querySelectorAll("[js-update-variant]");

    this.defaultVariantId = this.getAttribute("data-default-variant-id");
    this.currentProductId = this.getAttribute("data-current-product-id");
    this.currentVariantId = this._getVariantFromURL() || this.defaultVariantId;
    this.currentVariantAvailable =
      this.getAttribute("data-default-variant-available") === "true";
    this.currentProductHandle = this.getAttribute(
      "data-current-product-handle"
    );
    this.currentOptions = {
      option2: this.getAttribute("data-current-option2"),
      option3: this.getAttribute("data-current-option3"),
    };

    // Track user's preferred size choice throughout the PDP session
    this.preferredSize = null;
    // If variant is in URL, treat it as user-selected
    const urlParams = new URLSearchParams(window.location.search);
    this.hasUserSelectedSize = urlParams.has("variant");

    // Track current variant availability for AMP modal close protection
    this.currentRealTimeAvailable = null;

    // Performance: Cache product data to avoid redundant fetches
    this.productCache = new Map();
    this.htmlCache = new Map();

    this.isQuickView =
      this.dataset.isQuickView === "true" ||
      Boolean(this.closest("quick-view-modal"));

    if (this.isQuickView) {
      if (this.debug) console.log("ProductGrouping: quick-view mode enabled");
    }

    this.productGroupingData = {};
    this.dataProductGroup = JSON.parse(
      this.querySelector("script[data-product-group]")?.textContent || "{}"
    );

    // Prevent custom.js from interfering with our button state management
    this._preventVariantChangeInterference();

    this._checkAndSetDefaultVariant();
    this._bindEvents();
    this._setupSizeGuide();

    // CRITICAL: Set up button state guardian to prevent buttons from disappearing
    this._setupButtonStateGuardian();
  }

  _preventVariantChangeInterference() {
    // NOTE: We should NOT block variant:change events - other systems need them
    // The ProductGrouping component will dispatch its own variant:change events
    // and manage button state directly, ensuring consistency
  }

  _bindEvents() {
    this.optionsToRequestData.forEach((option) => {
      option.addEventListener("click", (event) =>
        this._handleOptionChange(event, option)
      );
    });
    this._bindVariantButtons();
  } 

  _bindVariantButtons() {
    this.buttonsToUpdateVariant.forEach(button => {
      // Store the bound handler for later removal
      const handler = (event) => this._handleUpdateVariant(event, button);
      button._variantClickHandler = handler;
      button.addEventListener('click', handler);
    });
  }

  _rebindVariantButtons() {
    this.buttonsToUpdateVariant.forEach(button => {
      if (button._variantClickHandler) {
        button.removeEventListener('click', button._variantClickHandler);
        button._variantClickHandler = null;
      }
    });
    this.buttonsToUpdateVariant = this.querySelectorAll("[js-update-variant]");
    this._bindVariantButtons();
  }

  _checkAndSetDefaultVariant() {
    if (!this.currentVariantId && this.defaultVariantId) {
      this._updateURLWithVariant(this.defaultVariantId);
      this.currentVariantId = this.defaultVariantId;
    }

    const scriptTagsData = this.querySelectorAll(
      "script[js-product-group-data]"
    );
    this.productGroupingData = Array.from(scriptTagsData).reduce(
      (acc, script) => {
        const data = JSON.parse(script.textContent || "{}");
        const key = script.getAttribute("data-product-id");
        if (!acc[key]) {
          acc[key] = { tag_options: [], variants: [] };
        }
        const existingData = acc[key];
        existingData.tag_options.push(...data.tag_options);
        const uniqueVariants = data.variants.filter(
          (newVariant) =>
            !existingData.variants.some(
              (existingVariant) => existingVariant.id === newVariant.id
            )
        );
        existingData.variants.push(...uniqueVariants);
        return acc;
      },
      {}
    );

    // Check real-time availability for the current variant on page load
    if (this.currentVariantId) {
      this._fetchVariantAvailability(this.currentVariantId)
        .then((realTimeAvailable) => {
          const addToCartForm = document.querySelector(
            ".product-info .shopify-product-form"
          );
          const addToCartButton = addToCartForm?.querySelector(
            'buy-buttons button[type="submit"]'
          );
          const notifyMeBtn = addToCartForm?.querySelector(
            "buy-buttons button[data-bis-trigger]"
          );
          const addToCartButtonText = addToCartButton?.querySelector("div");
          const soldOutElement = document.querySelector(
            ".product-info sold-out-badge"
          );

          if (addToCartButton && notifyMeBtn && addToCartButtonText) {
            if (!realTimeAvailable) {
              // If user hasn't selected a size, try to find first available
              if (!this.hasUserSelectedSize) {
                this._findFirstAvailableVariantAsync().then(
                  (firstAvailableVariant) => {
                    if (firstAvailableVariant) {
                      this._switchToVariant(firstAvailableVariant);
                    } else {
                      this._updateButtonVisibility(
                        addToCartButton,
                        notifyMeBtn,
                        addToCartButtonText,
                        soldOutElement,
                        false
                      );
                      this._setupWaitlistScroll();
                    }
                  }
                );
                return;
              }

              // User selected a specific unavailable size
              this._updateButtonVisibility(
                addToCartButton,
                notifyMeBtn,
                addToCartButtonText,
                soldOutElement,
                false
              );
              this._setupWaitlistScroll();
            } else {
              // Variant is available - ensure button is visible
              let currentVariant = this._getCurrentVariant?.();
              
              // If variant doesn't have price, try to get it from multiple sources
              if (currentVariant && (!currentVariant.price || currentVariant.price === null || currentVariant.price === undefined)) {
                // First, try to get price from variant picker script (most reliable)
                const variantScript = document.querySelector('variant-picker script[data-variant]');
                if (variantScript) {
                  try {
                    const variantFromScript = JSON.parse(variantScript.textContent || '{}');
                    if (variantFromScript && variantFromScript.id && variantFromScript.id.toString() === this.currentVariantId.toString() && variantFromScript.price) {
                      currentVariant.price = variantFromScript.price;
                      if (this.debug) {
                        console.log('[ProductGrouping] Got price from variant picker script:', currentVariant.price);
                      }
                    }
                  } catch (e) {
                    if (this.debug) {
                      console.warn('[ProductGrouping] Could not parse variant script:', e);
                    }
                  }
                }
                
                // If still no price, try to get from active button
                if ((!currentVariant.price || currentVariant.price === null || currentVariant.price === undefined)) {
                  const activeButton = document.querySelector('button[js-update-variant].is-active, button[js-update-variant][aria-checked="true"]');
                  if (activeButton && activeButton.dataset.variantPrice) {
                    const priceStr = activeButton.dataset.variantPrice;
                    const priceValue = parseFloat(priceStr);
                    if (!Number.isNaN(priceValue)) {
                      // Price is in dollars, convert to cents
                      currentVariant.price = Math.round(priceValue * 100);
                      if (this.debug) {
                        console.log('[ProductGrouping] Got price from active button:', currentVariant.price);
                      }
                    }
                  }
                }
                
                // If still no price, try to get from product grouping data
                if ((!currentVariant.price || currentVariant.price === null || currentVariant.price === undefined) && 
                    this.currentVariantId && this.productGroupingData && this.currentProductId) {
                  const productData = this.productGroupingData[this.currentProductId];
                  if (productData && productData.variants) {
                    const variantFromData = productData.variants.find(
                      v => v.id.toString() === this.currentVariantId.toString()
                    );
                    if (variantFromData && variantFromData.price) {
                      currentVariant.price = variantFromData.price;
                      if (this.debug) {
                        console.log('[ProductGrouping] Got price from product grouping data:', currentVariant.price);
                      }
                    }
                  }
                }
              }
              
              this._updateButtonVisibility(
                addToCartButton,
                notifyMeBtn,
                addToCartButtonText,
                soldOutElement,
                true,
                currentVariant
              );
              if (currentVariant && typeof currentVariant.price !== "undefined") {
                const cents =
                  typeof currentVariant.price === "string"
                    ? parseInt(currentVariant.price, 10)
                    : currentVariant.price;
                if (!Number.isNaN(cents)) {
                  const dollars = cents / 100;
                  const priceForQuickAdd = dollars % 1 === 0 ? dollars.toString() : dollars.toFixed(2);
                  if (priceForQuickAdd) {
                    this.updateQuickAddCTA(true, this.currentVariantId, priceForQuickAdd);
                  }
                }
              }

            }
          }
        })
        .catch((error) => {
          console.error(
            "[ProductGrouping] Failed to fetch initial variant availability:",
            error
          );
        });
    }

    //Force update the stock message on load
    const stockMessageEl = document.querySelector(
      'product-stock-message[data-context="pdp"]'
    );
    if (stockMessageEl && this.currentVariantId) {
      //console.log('[ProductGrouping] Initializing stock message for PDP with variant:', this.currentVariantId);
      stockMessageEl.updateVariantId(this.currentVariantId);
    }
  }
 
  _getVariantFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('variant');
  }

  _updateURLWithVariant(variantId) {
    if (this.isQuickView || !variantId) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  _handleUpdateVariant(event, button) {
    event.preventDefault();
    // console.log('Updating variant...', event, button);
    const newVariantId = button.dataset.variantId;
    const newVariantPrice = button.dataset.variantPrice;
    const isAvailable = button.dataset.available === "true";
    const optionIndex = button.dataset.optionIndex;
    const newValue = button.dataset.value;

    if (this.debug)
      if (this.debug) {
        console.log("Handling variant update:", {
          newVariantId,
          newVariantPrice,
          isAvailable,
          optionIndex,
          newValue,
        });
      }

    // Track user's size preference when they explicitly select a size
    if (this._isOptionSize(optionIndex, newValue)) {
      this.preferredSize = newValue;
      this.hasUserSelectedSize = true;
      if (this.debug)
        console.log("User selected size preference:", this.preferredSize);
    }

    if (!newVariantId || !optionIndex || !newValue) {
      console.error("Missing required button dataset attributes:", {
        newVariantId,
        optionIndex,
        newValue,
      });
      return;
    }

    const addToCartForm = document.querySelector(
      ".product-info .shopify-product-form"
    );
    const input = addToCartForm?.querySelector('input[name="id"]');
    const soldOutElement = document.querySelector(
      ".product-info sold-out-badge"
    );

    if (!addToCartForm || !input) {
      console.error("Add to cart form or input not found:", {
        addToCartForm: !!addToCartForm,
        input: !!input,
      });
      return;
    }

    this.currentOptions[`option${optionIndex}`] = newValue;
    this.currentVariantId = newVariantId;
    input.value = newVariantId;
    this.updateVariantClass();
    this._updateURLWithVariant(newVariantId);

    if (this.debug) {
      console.log("Updated currentOptions:", this.currentOptions);
      console.log("Updated currentVariantId:", this.currentVariantId);
    }

    // Notify AMP Back in Stock app about variant change
    this._notifyAmpBackInStock({
      id: newVariantId,
      available: isAvailable,
      price: newVariantPrice,
    });

    // CRITICAL: Dispatch variant:change event for other systems (custom.js, etc.)
    // This ensures all parts of the system stay in sync
    document.dispatchEvent(
      new CustomEvent("variant:change", {
        bubbles: true,
        detail: {
          variant: {
            id: newVariantId,
            available: isAvailable,
            price: newVariantPrice,
          },
          formId: addToCartForm?.id,
        },
      })
    );

    const stockMessages = [
      ...this.parentComponent.querySelectorAll("product-stock-message"),
      ...(this.parentComponent
        .closest("quick-view-modal")
        ?.querySelectorAll("product-stock-message") || []),
    ];
    stockMessages.forEach((stockMessage) => {
      stockMessage.updateVariantId(newVariantId);
    });

    // Fetch real-time variant availability to ensure consistency
    this._fetchVariantAvailability(newVariantId)
      .then((realTimeAvailable) => {
        if (this.debug) {
          console.log("[ProductGrouping] Real-time availability fetched:", {
            variantId: newVariantId,
            realTimeAvailable,
            cachedAvailable: isAvailable,
          });
        }
        // Re-query buttons fresh inside callback to avoid stale references
        // Don't filter by type="submit" since it may have been changed by third-party scripts
        const buyButtonsEl = addToCartForm?.querySelector("buy-buttons");
        const allButtons = buyButtonsEl
          ? Array.from(buyButtonsEl.querySelectorAll("button"))
          : [];

        if (this.debug) {
          console.log("[ProductGrouping] Found buttons:", {
            totalButtons: allButtons.length,
            buttons: allButtons.map((btn) => ({
              hasBisTrigger: btn.hasAttribute("data-bis-trigger"),
              name: btn.name,
              type: btn.type,
              id: btn.id,
              display: btn.style.display,
            })),
          });
        }

        const notifyMeBtn = allButtons.find((btn) =>
          btn.hasAttribute("data-bis-trigger")
        );
        const addToCartButton = allButtons.find(
          (btn) => !btn.hasAttribute("data-bis-trigger")
        );

        const addToCartButtonText = addToCartButton?.querySelector("div");

        // CRITICAL: Always ensure we have both buttons before proceeding
        if (!addToCartButton || !notifyMeBtn) {
          console.warn(
            "[ProductGrouping] Button elements not found, retrying...",
            { addToCartButton: !!addToCartButton, notifyMeBtn: !!notifyMeBtn }
          );
          // Retry once after a short delay
          setTimeout(() => {
            const retryBuyButtonsEl =
              addToCartForm?.querySelector("buy-buttons");
            const retryAllButtons = retryBuyButtonsEl
              ? Array.from(retryBuyButtonsEl.querySelectorAll("button"))
              : [];
            const retryNotifyMeBtn = retryAllButtons.find((btn) =>
              btn.hasAttribute("data-bis-trigger")
            );
            const retryAddToCartButton = retryAllButtons.find(
              (btn) => !btn.hasAttribute("data-bis-trigger")
            );
            const retryAddToCartButtonText =
              retryAddToCartButton?.querySelector("div");

            if (retryAddToCartButton && retryNotifyMeBtn) {
              this._updateButtonVisibility(
                retryAddToCartButton,
                retryNotifyMeBtn,
                retryAddToCartButtonText,
                soldOutElement,
                realTimeAvailable
              );
            }
          }, 50);
          return;
        }

        // Update button visibility using helper method to ensure consistency
        const variantForPrice = {
          id: newVariantId,
          // newVariantPrice is usually in dollars ("398" or "398.00")
          price: typeof newVariantPrice === "string"
            ? Math.round(Number(newVariantPrice) * 100)
            : newVariantPrice
        };

        this._updateButtonVisibility(
          addToCartButton,
          notifyMeBtn,
          addToCartButtonText,
          soldOutElement,
          realTimeAvailable,
          variantForPrice   // 🔹pass price here
        );


        this.updateQuickAddCTA(
          realTimeAvailable,
          newVariantId,
          newVariantPrice
        );
        setTimeout(() => {
          document
            .querySelectorAll('product-stock-message[data-context="pdp"]')
            .forEach((el) => {
              el.hasFetched = false; // Force it to re-fetch
              el.updateVariantId(newVariantId);
            });
        }, 100);
      })
      .catch((error) => {
        console.error(
          "[ProductGrouping] Failed to fetch real-time variant availability, falling back to cached data:",
          error
        );

        // Fallback to original cached logic if API fails
        // Re-query buttons to ensure we have fresh references
        const fallbackBuyButtonsEl =
          addToCartForm?.querySelector("buy-buttons");
        const fallbackAllButtons = fallbackBuyButtonsEl
          ? Array.from(fallbackBuyButtonsEl.querySelectorAll("button"))
          : [];
        const fallbackNotifyMeBtn = fallbackAllButtons.find((btn) =>
          btn.hasAttribute("data-bis-trigger")
        );
        const fallbackAddToCartButton = fallbackAllButtons.find(
          (btn) => !btn.hasAttribute("data-bis-trigger")
        );
        const fallbackAddToCartButtonText =
          fallbackAddToCartButton?.querySelector("div");
          const variantForPrice = {
            id: newVariantId,
            price: typeof newVariantPrice === "string"
              ? Math.round(Number(newVariantPrice) * 100)
              : newVariantPrice
          };

        if (fallbackAddToCartButton && fallbackNotifyMeBtn) {
          this._updateButtonVisibility(
            fallbackAddToCartButton,
            fallbackNotifyMeBtn,
            fallbackAddToCartButtonText,
            soldOutElement,
            isAvailable,
            variantForPrice 
          );
        }

        this.updateQuickAddCTA(isAvailable, newVariantId, newVariantPrice);
      });
  }

  /**
   * Central method to update button visibility - ensures consistent behavior
   * CRITICAL: This is the single source of truth for button state management
   */
  _updateButtonVisibility(
    addToCartButton,
    notifyMeBtn,
    addToCartButtonText,
    soldOutElement,
    isAvailable,
    variantForPrice = null
  ) {
    if (!addToCartButton || !notifyMeBtn) {
      console.error('[ProductGrouping] Cannot update button visibility - buttons not found');
      return;
    }

    if (this.debug) {
      console.log('[ProductGrouping] _updateButtonVisibility called', {
        isAvailable,
        hasSoldOutElement: !!soldOutElement,
        addToCartButtonDisplay: addToCartButton.style.display,
        notifyMeBtnDisplay: notifyMeBtn.style.display
      });
    }

    const isPreorder = window.isPreorderProduct?.() || false;

    // 🔹 Build price string once
    let priceText = '';
    try {
      let variant = variantForPrice || this._getCurrentVariant?.();
      
      if (this.debug) {
        console.log('[ProductGrouping] _updateButtonVisibility - variant:', {
          hasVariant: !!variant,
          variantId: variant?.id,
          hasPrice: !!variant?.price,
          price: variant?.price,
          variantForPrice: variantForPrice ? { id: variantForPrice.id, price: variantForPrice.price } : null
        });
      }
      
      // Get price directly from the displayed price-list element (matches what user sees)
      // This avoids parsing/formatting issues and ensures it always matches the displayed price
      const getPriceFromPriceList = () => {
        const productInfo = document.querySelector('.product-info, [class*="product-info"]');
        const priceList = productInfo 
          ? productInfo.querySelector('price-list.price-list')
          : document.querySelector('price-list.price-list');
        
        if (priceList) {
          // Get the sale-price element (it shows the actual price whether on sale or not)
          const salePriceEl = priceList.querySelector('sale-price');
          const priceElement = salePriceEl || priceList.querySelector('.text-subdued');
          
          if (priceElement) {
            // Get text content and remove screen reader text
            let priceTextContent = priceElement.textContent?.trim() || '';
            
            // Remove screen reader text if present
            const srOnlySpans = priceElement.querySelectorAll('.sr-only');
            srOnlySpans.forEach(span => {
              priceTextContent = priceTextContent.replace(span.textContent, '').trim();
            });
            
            // Extract just the price (handles formats like "$79.20", "$79", etc.)
            const priceMatch = priceTextContent.match(/\$?[\d,]+(?:\.\d{2})?/);
            if (priceMatch) {
              let extractedPrice = priceMatch[0];
              // Ensure it starts with $
              if (!extractedPrice.startsWith('$')) {
                extractedPrice = '$' + extractedPrice;
              }
              
              if (this.debug) {
                console.log('[ProductGrouping] Got price text directly from sale-price element:', extractedPrice);
              }
              
              return extractedPrice;
            }
          }
        }
        return null;
      };
      
      // Try to get price immediately
      priceText = getPriceFromPriceList();
      
      // If not found, wait for price-list to update (variant changes can be async)
      // The price-list might be re-rendered when variants change, so we need to wait
      if (!priceText) {
        // Use requestAnimationFrame to wait for DOM updates, then retry multiple times
        let attempts = 0;
        const maxAttempts = 5;
        
        const tryGetPrice = () => {
          attempts++;
          // Always query fresh - price-list might be replaced during color changes
          const retryPrice = getPriceFromPriceList();
          
          if (retryPrice) {
            priceText = retryPrice;
            // Update button text if we got the price
            if (addToCartButtonText && isAvailable) {
              if (isPreorder) {
                addToCartButtonText.textContent = `PREORDER – ${retryPrice}`;
              } else {
                addToCartButtonText.textContent = `Add to Bag – ${retryPrice}`;
              }
            }
          } else if (attempts < maxAttempts) {
            // Retry with increasing delays
            setTimeout(tryGetPrice, 50 * attempts);
          }
        };
        
        // Start retry attempts after DOM updates
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(tryGetPrice, 50);
          });
        });
      }
      
      // Fallback: if price-list not available, try to format from variant data
      if (!priceText && variant && typeof variant.price !== 'undefined' && variant.price !== null) {
        const cents =
          typeof variant.price === 'string'
            ? parseInt(variant.price, 10)
            : variant.price;
        if (!Number.isNaN(cents)) {
          priceText = (cents / 100).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
          if (this.debug) {
            console.log('[ProductGrouping] Price text formatted from variant data:', priceText);
          }
        }
      } else if (!priceText && this.debug) {
        console.warn('[ProductGrouping] No price found for variant:', variant);
      }
    } catch (err) {
      if (this.debug) {
        console.warn('[ProductGrouping] Error reading variant price for CTA label', err);
      }
    }

    if (isAvailable) {
      // Variant is available - ALWAYS show Add to Bag / PREORDER button
      if (addToCartButtonText) {
        if (isPreorder) {
          addToCartButtonText.textContent = priceText
            ? `PREORDER – ${priceText}`
            : 'PREORDER';
        } else {
          addToCartButtonText.textContent = priceText
            ? `Add to Bag – ${priceText}`
            : 'Add to Bag';
        }
      }

      // Ensure button visible/enabled
      addToCartButton.removeAttribute("disabled");
      addToCartButton.classList.remove("button--subdued");
      addToCartButton.setAttribute("type", "submit");
      addToCartButton.style.setProperty("display", "block", "important");
      addToCartButton.style.setProperty("visibility", "visible", "important");
      addToCartButton.style.setProperty("opacity", "1", "important");
      addToCartButton.style.removeProperty("pointer-events");

      // Hide waitlist
      notifyMeBtn.style.setProperty("display", "none", "important");
      notifyMeBtn.style.setProperty("visibility", "hidden", "important");
      notifyMeBtn.style.setProperty("opacity", "0", "important");

      if (soldOutElement) {
        soldOutElement.classList.add("is-hidden");
      }

      if (this.debug) {
        console.log("[ProductGrouping] Button visibility updated: available");
      }
    } else {
      // Variant is sold out - show Join Waitlist button
      this.showJoinWaitListButton(
        addToCartButton,
        notifyMeBtn,
        addToCartButtonText,
        soldOutElement
      );

      if (this.debug) {
        console.log(
          "[ProductGrouping] Button visibility updated: Join Waitlist shown"
        );
      }
    }
  }


  updateQuickAddCTA(isAvailable, newVariantId, newVariantPrice) {
    if (this.debug) {
      console.log("updateQuickAddCTA called:", {
        isAvailable,
        newVariantId,
        newVariantPrice,
      });
    }
    document
      .querySelectorAll("product-quick-add, buy-buttons")
      .forEach((quickAdd) => {
        const $submit = quickAdd.querySelector(".buy-buttons__submit");
        // console.log('🚀 -> $submit:', $submit)
        const input = quickAdd.querySelector('input[name="id"]');
        // console.log('🚀 -> input:', input)
        if (input) input.value = newVariantId;

        if (isAvailable && $submit) {
          const isPreorder = document
            .querySelector("#main-product-info")
            ?.classList.contains("preorder");
          
          // Format price correctly - newVariantPrice comes from data-variant-price
          // The money_without_trailing_zeros filter should output dollars (e.g., "75" for $75, "179" for $179)
          // But we need to handle edge cases where it might be incorrectly formatted
          let formattedPrice = '';
          if (newVariantPrice) {
            let priceValue = typeof newVariantPrice === 'string' 
              ? parseFloat(newVariantPrice.replace(/[^0-9.]/g, '')) 
              : newVariantPrice;
            
            if (!Number.isNaN(priceValue) && priceValue > 0) {
              // Detect incorrectly formatted prices that have been divided by 100:
              // - Prices like 0.75, 1.79, 7.50 should be 75, 179, 750
              // - Rule: If price is less than 20 and has exactly 2 decimal places, it's likely been incorrectly divided
              //   (This avoids false positives for legitimate prices like $1.99, $2.50, etc. which are rare in this store)
              const originalPrice = priceValue;
              const hasDecimals = priceValue % 1 !== 0;
              const decimalPlaces = priceValue.toString().split('.')[1]?.length || 0;
              
              // More aggressive detection: if price is suspiciously low with decimals, correct it
              // This catches cases like 0.75, 1.79, 7.50, etc.
              if (priceValue < 20 && hasDecimals && decimalPlaces === 2) {
                // Price has 2 decimal places and is less than $20 - likely incorrectly formatted
                // Multiply by 100 to correct it (e.g., 1.79 -> 179, 0.75 -> 75, 7.50 -> 750)
                priceValue = Math.round(priceValue * 100);
                if (this.debug) {
                  console.log('[ProductGrouping] Price corrected from', originalPrice, 'to', priceValue, '(was incorrectly divided by 100)');
                }
              } else if (priceValue < 1 && priceValue > 0) {
                // Price less than 1 (e.g., 0.75) - definitely needs correction
                priceValue = Math.round(priceValue * 100);
                if (this.debug) {
                  console.log('[ProductGrouping] Price corrected from', originalPrice, 'to', priceValue, '(was < 1)');
                }
              }
              
              formattedPrice = priceValue.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
            }
          }
          
          const newLabel = isPreorder
            ? (formattedPrice ? `PREORDER – ${formattedPrice}` : 'PREORDER')
            : (formattedPrice ? `Add to Bag – ${formattedPrice}` : 'Add to Bag');
          if (this.debug) {
            console.log("Setting new label:", newLabel);
          }

          // Handle both structures: buttons with <div> wrapper (quick-add) and plain text buttons (PDP)
          const $labelElement = $submit.querySelector("div");
          if ($labelElement) {
            $labelElement.textContent = newLabel;
          } else {
            $submit.textContent = newLabel;
          }
          if (this.debug) {
            console.log("Label updated successfully");
          }
          $submit.setAttribute("type", "submit");
          $submit.removeEventListener("click", this._scrollToBuyButtons);
        } else if ($submit) {
          const $labelElement = $submit.querySelector("div");
          if ($labelElement) {
            $labelElement.textContent = "Join Waitlist";
          } else {
            $submit.textContent = "Join Waitlist";
          }
          $submit.setAttribute("type", "button");
          $submit.addEventListener("click", this._scrollToBuyButtons);
        }
      });
  }

  _setupWaitlistScroll() {
    const $submit = document.querySelectorAll("product-quick-add .buy-buttons__submit");
    $submit.forEach((submit) => {
      submit.addEventListener("click", this._scrollToBuyButtons);
    });
  }

  _scrollToBuyButtons(event) {
    event.preventDefault();
    const scrollToElement = document.querySelector('.product-info__buy-buttons');
    if (scrollToElement) {
      scrollToElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  updateVariantClass() {
    Object.values(this.currentOptions).forEach((data, index) => {
      // console.log(' this.currentOptions ', index, this.currentOptions)

      let optionIndex = index;
      if (this.currentOptions.option2.length) {
        optionIndex = optionIndex + 1
      }
      if (this.currentOptions.option3.length) {
        optionIndex = optionIndex + 1
      }
      // console.log('🚀 -> optionIndex:', optionIndex)
      const row = this.querySelector(`.product-grouping__options[data-options-row="${optionIndex}"]`);
      // console.log('🚀 -> row:', row)

      if (optionIndex === 1) {
        row?.querySelectorAll('button[js-update-variant]').forEach(button => {
          const isActive = button.dataset.value === this.currentOptions[`option${optionIndex}`];
          button.classList.toggle('is-active', isActive);
        });
      }
      if (optionIndex === 2) {
        row?.querySelectorAll('button[js-update-variant]').forEach(button => {
          const isVisible = button.dataset.option3 === this.currentOptions.option3;
          const isActive = button.dataset.value === this.currentOptions[`option${optionIndex}`];
          button.classList.toggle('is-active', isActive);
          button.classList.toggle('is-hidden', !isVisible);
        });
      }
      if (optionIndex === 3) {
        row?.querySelectorAll('button[js-update-variant]').forEach(button => {
          const isVisible = button.dataset.option2 === this.currentOptions.option2;
          const isActive = button.dataset.value === this.currentOptions[`option${optionIndex}`];
          button.classList.toggle('is-active', isActive);
          button.classList.toggle('is-hidden', !isVisible);
        });
      }
    });
  }

  showJoinWaitListButton(addToCartButton, notifyMeBtn, addToCartButtonText, soldOutElement) {
    if (!addToCartButton || !notifyMeBtn) {
      console.warn(
        "[ProductGrouping] showJoinWaitListButton called with missing buttons"
      );
      return;
    }

    // CRITICAL: Completely hide and disable Add to Cart button
    // We need to be aggressive here to override any CSS rules
    addToCartButton.setAttribute("disabled", "disabled");
    addToCartButton.classList.add("button--subdued");
    addToCartButton.setAttribute("type", "button"); // Remove submit capability
    addToCartButton.style.setProperty("display", "none", "important");
    addToCartButton.style.setProperty("visibility", "hidden", "important");
    addToCartButton.style.setProperty("opacity", "0", "important");
    addToCartButton.style.setProperty("pointer-events", "none", "important");

    if (addToCartButtonText) {
      addToCartButtonText.textContent = "Sold Out";
    }

    // Show waitlist button with !important to override any interference
    notifyMeBtn.style.setProperty("display", "block", "important");
    notifyMeBtn.style.setProperty("visibility", "visible", "important");
    notifyMeBtn.style.setProperty("opacity", "1", "important");

    // Show sold out badge
    if (soldOutElement) {
      soldOutElement.classList.remove("is-hidden");
    }

    if (this.debug) {
      if (this.debug) {
        console.log("[ProductGrouping] Waitlist button shown for OOS variant");
      }
    }
  }

  async _fetchVariantStock(variantId) {
    const accessToken = window.__VPV?.shop?.storefrontToken;
    const store = window.__VPV?.shop?.name;
    const locale = window.__VPV?.shop?.locale || 'en-US';

    if (!accessToken || !store) {
      console.error('[ProductGrouping] Missing Shopify token or store name');
      return 0;
    }

    const query = `
      query GetVariantStock($variantId: ID!) {
        node(id: $variantId) {
          ... on ProductVariant {
            id
            quantityAvailable
          }
        }
      }
    `;

    const variables = { variantId: `gid://shopify/ProductVariant/${variantId}` };

    try {
      const response = await fetch(`https://${store}.myshopify.com/api/2025-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken,
          'Accept': 'application/json',
          'Accept-Language': locale,
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) {
        console.error(`[ProductGrouping] Shopify API error: ${response.status}`, await response.text());
        return 0;
      }

      const result = await response.json();
      const qty = result?.data?.node?.quantityAvailable ?? 0;
      return qty;
    } catch (error) {
      console.error('[ProductGrouping] Error fetching stock quantity:', error);
      return 0;
    }
  }

  async _fetchVariantAvailability(variantId) {
    const accessToken = window.__VPV?.shop?.storefrontToken;
    const store = window.__VPV?.shop?.name;
    const locale = window.__VPV?.shop?.locale || 'en-US';

    if (!accessToken || !store) {
      console.error('[ProductGrouping] Missing Shopify token or store name for availability check');
      return false;
    }

    const query = `
      query GetVariantAvailability($variantId: ID!) {
        node(id: $variantId) {
          ... on ProductVariant {
            id
            availableForSale
          }
        }
      }
    `;

    const variables = { variantId: `gid://shopify/ProductVariant/${variantId}` };

    try {
      const response = await fetch(`https://${store}.myshopify.com/api/2025-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken,
          'Accept': 'application/json',
          'Accept-Language': locale,
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) {
        console.error(`[ProductGrouping] Shopify API error for availability: ${response.status}`, await response.text());
        return false;
      }

      const result = await response.json();
      const available = result?.data?.node?.availableForSale ?? false;
      
      if (this.debug) {
        console.log('[ProductGrouping] API Response for variant', variantId, ':', {
          fullResult: result,
          availableForSale: available,
          node: result?.data?.node
        });
      }
      
      return available;
    } catch (error) {
      console.error('[ProductGrouping] Error fetching real-time variant availability:', error);
      return false;
    }
  }

  _notifyAmpBackInStock(variant) {
    try {
      // Method 1: Update the variant input that the app might be watching
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput && variant.id) {
        variantInput.value = variant.id;
        // Trigger change event to notify any listeners
        variantInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      // Method 2: Update data attributes that the app reads when the modal opens
      const bisButtons = document.querySelectorAll("[data-bis-trigger]");
      bisButtons.forEach((button) => {
        button.setAttribute("data-variant-id", variant.id);
        button.setAttribute(
          "data-product-id",
          variant.product_id || window.meta?.product?.id || ""
        );
      });
    } catch (error) {
      console.warn(
        "[ProductGrouping] Error notifying AMP Back in Stock app:",
        error
      );
    }
  }

  async _handleOptionChange(event, option) {
    event.preventDefault();
    if (option.classList.contains('is-disabled')) return;

    const newProduct = option.dataset.productHandle;
    const loadedIds = this.dataProductGroup?.loaded_ids?.replace(/"/g, '') || false;

    if (newProduct && newProduct !== this.currentProductHandle) {
      try {
        this.parentComponent.classList.add("is-loading");

        // Capture current option2 and option3 values to preserve across color changes
        const currentVariant = this._getCurrentVariant();
        const targetOption2 = currentVariant?.option2;
        const targetOption3 = currentVariant?.option3;

        // Performance: Check cache first to avoid redundant fetches
        let productData;
        if (this.productCache.has(newProduct)) {
          productData = this.productCache.get(newProduct);
          if (this.debug)
            console.log(
              "[ProductGrouping] Using cached product data for:",
              newProduct
            );
        } else {
          // First, get the new product data to find matching variant
          let previewUrl = new URL(
            window.location.origin + `/products/${newProduct}.json`
          );
          const productResponse = await fetch(previewUrl);

          // Validate response before parsing
          if (!productResponse.ok) {
            console.error(
              `[ProductGrouping] Failed to fetch product: ${productResponse.status}`
            );
            throw new Error(`Product fetch failed: ${productResponse.status}`);
          }

          const contentType = productResponse.headers.get("content-type");
          if (!contentType || !contentType.includes("application/json")) {
            console.error(
              `[ProductGrouping] Invalid content-type: ${contentType}`
            );
            throw new Error("Product response is not JSON");
          }

          productData = await productResponse.json();

          // Cache for future clicks
          this.productCache.set(newProduct, productData);
          if (this.debug)
            console.log(
              "[ProductGrouping] Cached product data for:",
              newProduct
            );
        }

        // Find variant with matching option2/option3 in the new product
        let targetVariantId = null;
        if (
          productData.product &&
          productData.product.variants &&
          (targetOption2 || targetOption3)
        ) {
          targetVariantId = this._findMatchingVariant(
            productData.product.variants,
            targetOption2,
            targetOption3
          );
        }

        let url = new URL(window.location.origin + `/products/${newProduct}`);
        let params = new URLSearchParams();
        params.set("view", "product-info-ajax");
        params.set("section_id", this.sectionId);
        params.set("no-fade-in", "true");
        if (loadedIds) {
          params.set("product-group-ids", loadedIds);
        }

        // If we found a matching variant, include it in the request so the new product renders with correct size
        if (targetVariantId) {
          params.set("variant", targetVariantId);
          if (this.debug)
            console.log("Including variant in AJAX request:", targetVariantId);
        }

        url.search = params.toString();
        const decodedURL = url.toString().replace(/%2B/g, "+");
        if (this.debug) console.log("decodedURL:", decodedURL);

        // Performance: Cache HTML to avoid redundant fetches
        const cacheKey = `${newProduct}-${targetVariantId || "default"}`;
        let text;
        if (this.htmlCache.has(cacheKey)) {
          text = this.htmlCache.get(cacheKey);
          if (this.debug)
            console.log("[ProductGrouping] Using cached HTML for:", cacheKey);
        } else {
          const response = await fetch(decodedURL);
          text = await response.text();

          // Cache for future clicks
          this.htmlCache.set(cacheKey, text);
          if (this.debug)
            console.log("[ProductGrouping] Cached HTML for:", cacheKey);
        }

        const parser = new DOMParser();
        const newDocument = parser.parseFromString(text, "text/html");

        const newMainProduct = newDocument.querySelector("#main-product-info");
        const currentMainProduct = this.isQuickView
          ? document.querySelector("quick-view-modal .quick-add-modal")
          : document.querySelector("#main-product-info");

        this.firstVariantId =
          newMainProduct?.getAttribute("data-first-variant");
        this.productId = newMainProduct?.getAttribute("data-product-id");
        this.currentProductHandle = newProduct;
        this.currentProductId = this.productId;

        // Use the target variant if we found one
        // If not found, don't set to a random variant - let the code in requestAnimationFrame
        // find the correct matching variant based on preserved size
        if (targetVariantId) {
          this.currentVariantId = targetVariantId;
        } else {
          // Don't set to a random available variant - we'll find the right one in requestAnimationFrame
          // But we need to set something for the form, so use firstVariantId as a placeholder
          // The requestAnimationFrame code will update it to the correct matching variant
          this.currentVariantId = this.firstVariantId;
          if (this.debug) {
            console.log('[ProductGrouping] No matching variant found initially, will search for matching variant in requestAnimationFrame');
          }
        }

        if (newMainProduct && currentMainProduct) {
          this.isQuickView
            ? (currentMainProduct.innerHTML = newMainProduct.innerHTML)
            : currentMainProduct.replaceWith(newMainProduct);
        }

        // Update the variant input field with the correct variant ID
        const variantInput = document.querySelector(
          '.product-info input[name="id"]'
        );
        if (variantInput && this.currentVariantId) {
          variantInput.value = this.currentVariantId;
        }

        // Re-bind variant buttons
        this._rebindVariantButtons();

        // Update product grouping data from the new HTML
        this.dataProductGroup = JSON.parse(
          this.querySelector("script[data-product-group]")?.textContent || "{}"
        );

        // Rebuild productGroupingData to include the new product's variant data
        const scriptTagsData = this.querySelectorAll(
          "script[js-product-group-data]"
        );
        this.productGroupingData = Array.from(scriptTagsData).reduce(
          (acc, script) => {
            const data = JSON.parse(script.textContent || "{}");
            const key = script.getAttribute("data-product-id");
            if (!acc[key]) {
              acc[key] = { tag_options: [], variants: [] };
            }
            const existingData = acc[key];
            existingData.tag_options.push(...data.tag_options);
            const uniqueVariants = data.variants.filter(
              (newVariant) =>
                !existingData.variants.some(
                  (existingVariant) => existingVariant.id === newVariant.id
                )
            );
            existingData.variants.push(...uniqueVariants);
            return acc;
          },
          this.productGroupingData || {}
        );

        // Also add the new product data directly from our API fetch
        if (productData.product && this.currentProductId) {
          this.productGroupingData[this.currentProductId] = {
            tag_options: [],
            variants: productData.product.variants || [],
          };
        }

        // Update currentOptions to reflect the selected variant using data from API
        const selectedVariant = productData.product?.variants?.find(
          (v) => v.id.toString() === this.currentVariantId.toString()
        );
        if (selectedVariant) {
          this.currentOptions.option2 =
            selectedVariant.option2 || this.currentOptions.option2;
          this.currentOptions.option3 =
            selectedVariant.option3 || this.currentOptions.option3;
        }

        // Update the visual state of size/option buttons to reflect the selected variant
        this.updateVariantClass();

        // CRITICAL: Wait for DOM to fully render before querying buttons
        // Use requestAnimationFrame to ensure the replaced HTML is painted
        requestAnimationFrame(() => {
          // Reset button state for the new product and check availability
          const newAddToCartForm = document.querySelector(
            ".product-info .shopify-product-form"
          );
          const newSoldOutElement = document.querySelector(
            ".product-info sold-out-badge"
          );

          // Get the currently selected waist and inseam from DOM (what user actually selected)
          // Priority: 1. Active DOM buttons, 2. Current variant, 3. Preserved values, 4. currentOptions
          let selectedWaist = null;
          let selectedInseam = null;
          
          // First: Try to get from active variant buttons in DOM (most reliable - what user actually selected)
          const activeWaistButton = document.querySelector('button[js-update-variant][data-option2].is-active, button[js-update-variant][data-option2][aria-checked="true"]');
          const activeInseamButton = document.querySelector('button[js-update-variant][data-option3].is-active, button[js-update-variant][data-option3][aria-checked="true"]');
          
          if (activeWaistButton) {
            selectedWaist = activeWaistButton.dataset.option2;
          }
          if (activeInseamButton) {
            selectedInseam = activeInseamButton.dataset.option3;
          }
          
          // Second: Try to get from the currently selected variant in the form
          if (!selectedWaist || !selectedInseam) {
            const variantInput = document.querySelector('.product-info input[name="id"]');
            if (variantInput && variantInput.value) {
              const currentVariantId = variantInput.value;
              const currentVariant = productData.product?.variants?.find(
                v => v.id.toString() === currentVariantId.toString()
              ) || this.productGroupingData[this.currentProductId]?.variants?.find(
                v => v.id.toString() === currentVariantId.toString()
              );
              
              if (currentVariant) {
                if (!selectedWaist) selectedWaist = currentVariant.option2;
                if (!selectedInseam) selectedInseam = currentVariant.option3;
              }
            }
          }
          
          // Third: Fallback to preserved values from before color change
          if (!selectedWaist) {
            selectedWaist = targetOption2;
          }
          if (!selectedInseam) {
            selectedInseam = targetOption3;
          }
          
          // Final fallback to currentOptions
          if (!selectedWaist) {
            selectedWaist = this.currentOptions.option2;
          }
          if (!selectedInseam) {
            selectedInseam = this.currentOptions.option3;
          }
          
          if (this.debug) {
            console.log('[ProductGrouping] Selected size for variant search:', {
              waist: selectedWaist,
              inseam: selectedInseam,
              source: {
                fromDOMButtons: {
                  waist: !!activeWaistButton,
                  inseam: !!activeInseamButton
                },
                fromPreserved: {
                  waist: selectedWaist === targetOption2,
                  inseam: selectedInseam === targetOption3
                }
              }
            });
          }
          
          // FIRST: Check cached data to find available variant matching BOTH waist and inseam
          // This is faster and more reliable than waiting for real-time check
          let firstAvailableVariantFromCache = null;
          if (productData.product?.variants && selectedWaist && selectedInseam) {
            // Use normalization to handle quotes and whitespace differences
            const normalizeValue = (val) => val ? val.toString().replace(/["'"\s]/g, '').toLowerCase() : '';
            const normalizedSelectedWaist = normalizeValue(selectedWaist);
            const normalizedSelectedInseam = normalizeValue(selectedInseam);
            
            if (this.debug) {
              // Show sample of variants to see their actual values
              const sampleVariants = productData.product.variants.slice(0, 10).map(v => ({
                id: v.id,
                option2: v.option2,
                option3: v.option3,
                available: v.available,
                normalized: {
                  option2: normalizeValue(v.option2),
                  option3: normalizeValue(v.option3)
                }
              }));
              
              // Find all variants with matching waist to see what inseams are available
              const waistMatches = productData.product.variants.filter(v => {
                const normalizedVariantWaist = normalizeValue(v.option2);
                return normalizedVariantWaist === normalizedSelectedWaist;
              });
              
              console.log('[ProductGrouping] Cached check - searching for variant:', {
                selectedWaist,
                selectedInseam,
                normalizedSelectedWaist,
                normalizedSelectedInseam,
                totalVariants: productData.product.variants.length,
                sampleVariants,
                waistMatchesCount: waistMatches.length,
                waistMatches: waistMatches.map(v => ({
                  id: v.id,
                  option2: v.option2,
                  option3: v.option3,
                  available: v.available,
                  normalizedOption3: normalizeValue(v.option3),
                  matchesInseam: normalizeValue(v.option3) === normalizedSelectedInseam
                }))
              });
            }
            
            firstAvailableVariantFromCache = productData.product.variants.find(
              (v) => {
                // Normalize variant values for comparison
                const normalizedVariantWaist = normalizeValue(v.option2);
                const normalizedVariantInseam = normalizeValue(v.option3);
                
                const matchesWaist = normalizedVariantWaist === normalizedSelectedWaist;
                const matchesInseam = normalizedVariantInseam === normalizedSelectedInseam;
                const isAvailable = v.available === true || v.available === "true" || v.available === 1 || v.available === "1";
                
                if (this.debug && matchesWaist && matchesInseam) {
                  console.log('[ProductGrouping] Checking cached variant for availability (first check):', {
                    variantId: v.id,
                    available: v.available,
                    isAvailable: isAvailable,
                    variantWaist: v.option2,
                    variantInseam: v.option3,
                    selectedWaist: selectedWaist,
                    selectedInseam: selectedInseam,
                    normalized: {
                      variantWaist: normalizedVariantWaist,
                      variantInseam: normalizedVariantInseam,
                      selectedWaist: normalizedSelectedWaist,
                      selectedInseam: normalizedSelectedInseam
                    }
                  });
                }
                
                return isAvailable && matchesWaist && matchesInseam;
              }
            );
            
            if (firstAvailableVariantFromCache && this.debug) {
              console.log(
                "[ProductGrouping] Found available variant from cache matching both waist and inseam:",
                {
                  id: firstAvailableVariantFromCache.id,
                  option2: firstAvailableVariantFromCache.option2,
                  option3: firstAvailableVariantFromCache.option3,
                  available: firstAvailableVariantFromCache.available
                }
              );
            } else if (this.debug && productData.product?.variants) {
              // Log why no variant was found - show sample variants for debugging
              const normalizeValue = (val) => val ? val.toString().replace(/["'"\s]/g, '').toLowerCase() : '';
              const normalizedSelectedWaist = normalizeValue(selectedWaist);
              const normalizedSelectedInseam = normalizeValue(selectedInseam);
              
              const matchingVariants = productData.product.variants.filter(v => {
                const normalizedVariantWaist = normalizeValue(v.option2);
                const normalizedVariantInseam = normalizeValue(v.option3);
                return normalizedVariantWaist === normalizedSelectedWaist && normalizedVariantInseam === normalizedSelectedInseam;
              });
              
              // Show sample of variants with option2 matching waist
              const waistMatches = productData.product.variants.filter(v => {
                const normalizedVariantWaist = normalizeValue(v.option2);
                return normalizedVariantWaist === normalizedSelectedWaist;
              }).slice(0, 5);
              
              console.log('[ProductGrouping] Cached check - no available variant found:', {
                selectedWaist,
                selectedInseam,
                normalizedSelectedWaist,
                normalizedSelectedInseam,
                totalVariants: productData.product.variants.length,
                matchingVariantsCount: matchingVariants.length,
                matchingVariants: matchingVariants.map(v => ({
                  id: v.id,
                  option2: v.option2,
                  option3: v.option3,
                  available: v.available,
                  normalized: {
                    option2: normalizeValue(v.option2),
                    option3: normalizeValue(v.option3)
                  }
                })),
                sampleWaistMatches: waistMatches.map(v => ({
                  id: v.id,
                  option2: v.option2,
                  option3: v.option3,
                  normalized: {
                    option2: normalizeValue(v.option2),
                    option3: normalizeValue(v.option3)
                  }
                }))
              });
            }
          }
          
          // If we found an available variant in cache, use it immediately
          if (firstAvailableVariantFromCache) {
            this.currentVariantId = firstAvailableVariantFromCache.id.toString();
            
            // Update variant input
            const variantInput = document.querySelector('.product-info input[name="id"]');
            if (variantInput) {
              variantInput.value = this.currentVariantId;
            }
            
            // Update current options
            if (firstAvailableVariantFromCache.option2) {
              this.currentOptions.option2 = firstAvailableVariantFromCache.option2;
            }
            if (firstAvailableVariantFromCache.option3) {
              this.currentOptions.option3 = firstAvailableVariantFromCache.option3;
            }
            
            // Update visual state
            this.updateVariantClass();
            this._updateURLWithVariant(this.currentVariantId);
            
            // Update button state immediately with cached availability
            setTimeout(() => {
              const form = document.querySelector(".product-info .shopify-product-form");
              const soldOut = document.querySelector(".product-info sold-out-badge");
              if (form) {
                this._updateButtonStateAfterColorChange(
                  form,
                  soldOut,
                  true, // Use cached availability
                  firstAvailableVariantFromCache
                );
              }
            }, 100);
            
            // Verify with real-time check in background (but don't downgrade)
            this._fetchVariantAvailability(this.currentVariantId).then((realTimeAvailable) => {
              if (this.debug) {
                console.log("[ProductGrouping] Real-time check for cached variant:", realTimeAvailable);
              }
              // Only update if real-time confirms availability, don't downgrade
              if (realTimeAvailable === true) {
                const form = document.querySelector(".product-info .shopify-product-form");
                const soldOut = document.querySelector(".product-info sold-out-badge");
                if (form) {
                  this._updateButtonStateAfterColorChange(
                    form,
                    soldOut,
                    true,
                    firstAvailableVariantFromCache
                  );
                }
              }
            }).catch(() => {
              // On error, trust cached data
            });
            
            return; // Exit early, we've handled it
          }

          // Declare these in broader scope so they're accessible later
          let firstAvailableButton = null;
          let firstAvailableVariant = null;

          // CRITICAL: Fetch real-time availability for the selected variant after color change
          if (this.currentVariantId) {
            this._fetchVariantAvailability(this.currentVariantId)
              .then((realTimeAvailable) => {
                if (this.debug) {
                  console.log(
                    "[ProductGrouping] Color change - real-time availability:",
                    {
                      variantId: this.currentVariantId,
                      realTimeAvailable,
                    }
                  );
                }

                // If the preserved size variant is available, update button to show "Add to Bag"
                if (realTimeAvailable === true) {
                  // Variant is available - update button state
                  setTimeout(() => {
                    const form = document.querySelector(".product-info .shopify-product-form");
                    const soldOut = document.querySelector(".product-info sold-out-badge");
                    if (form) {
                      // Get current variant for price display
                      const currentVariant = productData.product?.variants?.find(
                        v => v.id.toString() === this.currentVariantId?.toString()
                      ) || this.productGroupingData[this.currentProductId]?.variants?.find(
                        v => v.id.toString() === this.currentVariantId?.toString()
                      );
                      
                      if (currentVariant) {
                        this._updateButtonStateAfterColorChange(
                          form,
                          soldOut,
                          true, // Available
                          currentVariant
                        );
                      }
                    }
                  }, 100);
                  return; // Exit early, we've handled it
                }
                
                // If the preserved size variant is not available, try to find variant matching BOTH waist and inseam
                if (!realTimeAvailable) {
                  
                  if (this.debug) {
                    console.log(
                      "[ProductGrouping] Looking for available variant matching BOTH waist and inseam:",
                      {
                        selectedWaist,
                        selectedInseam
                      }
                    );
                  }

                  // Only find variants that match BOTH waist and inseam - no fallbacks
                  const variantButtons = document.querySelectorAll('button[js-update-variant]');
                  
                  if (variantButtons.length > 0 && selectedWaist && selectedInseam) {
                    // Use normalization to handle quotes and whitespace differences
                    const normalizeValue = (val) => val ? val.toString().replace(/["'"\s]/g, '').toLowerCase() : '';
                    const normalizedSelectedWaist = normalizeValue(selectedWaist);
                    const normalizedSelectedInseam = normalizeValue(selectedInseam);
                    
                    // Must match BOTH waist and inseam (normalized)
                    firstAvailableButton = Array.from(variantButtons).find(btn => {
                      const available = btn.dataset.available;
                      const normalizedButtonWaist = normalizeValue(btn.dataset.option2);
                      const normalizedButtonInseam = normalizeValue(btn.dataset.option3);
                      const matchesWaist = normalizedButtonWaist === normalizedSelectedWaist;
                      const matchesInseam = normalizedButtonInseam === normalizedSelectedInseam;
                      
                      // Check availability - handle string 'true', boolean true, or '1'
                      const isAvailable = available === 'true' || available === true || available === '1' || available === 1;
                      
                      if (this.debug && matchesWaist && matchesInseam) {
                        console.log('[ProductGrouping] Checking button availability:', {
                          variantId: btn.dataset.variantId,
                          available: available,
                          availableType: typeof available,
                          isAvailable: isAvailable,
                          matchesWaist,
                          matchesInseam
                        });
                      }
                      
                      return isAvailable && matchesWaist && matchesInseam;
                    });
                    
                    if (this.debug) {
                      if (firstAvailableButton) {
                        console.log(
                          "[ProductGrouping] Found variant matching both waist and inseam:",
                          firstAvailableButton.dataset.variantId,
                          "Waist:",
                          firstAvailableButton.dataset.option2,
                          "Inseam:",
                          firstAvailableButton.dataset.option3
                        );
                      } else {
                        console.log(
                          "[ProductGrouping] No available variant found matching both waist and inseam:",
                          "Waist:",
                          selectedWaist,
                          "Inseam:",
                          selectedInseam,
                          "- Will show Join Waitlist"
                        );
                      }
                    }
                  } else if (this.debug) {
                    console.log(
                      "[ProductGrouping] Cannot search for matching variant - missing waist or inseam:",
                      {
                        hasWaist: !!selectedWaist,
                        hasInseam: !!selectedInseam,
                        hasButtons: variantButtons.length > 0
                      }
                    );
                  }

                  // Fallback: try from cached product data (must also match both waist and inseam)
                  if (!firstAvailableButton && productData.product?.variants && selectedWaist && selectedInseam) {
                    if (this.debug) {
                      console.log(
                        "[ProductGrouping] Checking cached product data for variants matching both waist and inseam. Total variants:",
                        productData.product.variants.length
                      );
                    }
                    
                    // Find first available variant that matches BOTH waist and inseam
                    // Use normalization to handle quotes and whitespace differences
                    const normalizeValue = (val) => val ? val.toString().replace(/["'"\s]/g, '').toLowerCase() : '';
                    const normalizedSelectedWaist = normalizeValue(selectedWaist);
                    const normalizedSelectedInseam = normalizeValue(selectedInseam);
                    
                    firstAvailableVariant = productData.product.variants.find(
                      (v) => {
                        // Normalize variant values for comparison
                        const normalizedVariantWaist = normalizeValue(v.option2);
                        const normalizedVariantInseam = normalizeValue(v.option3);
                        
                        // Must match both waist and inseam (normalized) AND be available
                        const matchesWaist = normalizedVariantWaist === normalizedSelectedWaist;
                        const matchesInseam = normalizedVariantInseam === normalizedSelectedInseam;
                        // Check availability - handle boolean true, string 'true', or number 1
                        const isAvailable = v.available === true || v.available === "true" || v.available === 1 || v.available === "1";
                        
                        if (this.debug && matchesWaist && matchesInseam) {
                          console.log('[ProductGrouping] Checking variant availability:', {
                            variantId: v.id,
                            available: v.available,
                            availableType: typeof v.available,
                            isAvailable: isAvailable,
                            matchesWaist,
                            matchesInseam,
                            variantWaist: v.option2,
                            variantInseam: v.option3,
                            selectedWaist: selectedWaist,
                            selectedInseam: selectedInseam,
                            normalized: {
                              variantWaist: normalizedVariantWaist,
                              variantInseam: normalizedVariantInseam,
                              selectedWaist: normalizedSelectedWaist,
                              selectedInseam: normalizedSelectedInseam
                            }
                          });
                        }
                        
                        return isAvailable && matchesWaist && matchesInseam;
                      }
                    );

                    if (this.debug) {
                      if (firstAvailableVariant) {
                        console.log(
                          "[ProductGrouping] Found variant from cached data matching both waist and inseam:",
                          {
                            id: firstAvailableVariant.id,
                            available: firstAvailableVariant.available,
                            option2: firstAvailableVariant.option2,
                            option3: firstAvailableVariant.option3
                          }
                        );
                      } else {
                        console.log(
                          "[ProductGrouping] No available variant in cached data matching both waist and inseam"
                        );
                      }
                    }
                  }

                  // Use button data if found, otherwise use variant data
                  if (firstAvailableButton) {
                    const variantId = firstAvailableButton.dataset.variantId;
                    const variantPrice = firstAvailableButton.dataset.variantPrice;
                    const option2 = firstAvailableButton.dataset.option2;
                    const option3 = firstAvailableButton.dataset.option3;
                    
                    if (this.debug) {
                      console.log(
                        "[ProductGrouping] Found available variant from DOM button:",
                        variantId,
                        "Available:",
                        firstAvailableButton.dataset.available
                      );
                    }

                    // Switch to the first available variant
                    this.currentVariantId = variantId;
                    
                    // Update variant input
                    const variantInput = document.querySelector(
                      '.product-info input[name="id"]'
                    );
                    if (variantInput) {
                      variantInput.value = this.currentVariantId;
                    }

                    // Update current options to match the new variant
                    if (option2) {
                      this.currentOptions.option2 = option2;
                    }
                    if (option3) {
                      this.currentOptions.option3 = option3;
                    }

                    // Update visual state of size/option buttons
                    this.updateVariantClass();

                    // Update URL with new variant
                    this._updateURLWithVariant(this.currentVariantId);

                    // Create variant object for button update
                    const variantForUpdate = {
                      id: variantId,
                      price: variantPrice ? (typeof variantPrice === 'string' ? Math.round(parseFloat(variantPrice) * 100) : variantPrice) : null,
                      option2: option2,
                      option3: option3,
                      available: true
                    };

                    // Use cached availability (true) since we found a variant with available === true
                    // The real-time check might fail or be delayed, so trust the cached data
                    // Update button visibility immediately with the cached availability
                    // Use a small delay to ensure DOM is fully updated
                    setTimeout(() => {
                      // Re-query form and elements to ensure we have fresh references
                      const form = document.querySelector(
                        ".product-info .shopify-product-form"
                      );
                      const soldOut = document.querySelector(
                        ".product-info sold-out-badge"
                      );
                      if (form) {
                        this._updateButtonStateAfterColorChange(
                          form,
                          soldOut,
                          true, // Use cached availability - we know this variant is available
                          variantForUpdate
                        );
                      } else if (this.debug) {
                        console.warn(
                          "[ProductGrouping] Form not found when trying to update button state"
                        );
                      }
                    }, 100);
                    
                    // Optionally verify with real-time check in the background (but don't block on it)
                    // We trust the cached data since we found a variant with available === true
                    // Only update if real-time check confirms availability (don't downgrade to unavailable)
                    this._fetchVariantAvailability(this.currentVariantId).then(
                      (newRealTimeAvailable) => {
                        if (this.debug) {
                          console.log(
                            "[ProductGrouping] Real-time availability check result:",
                            newRealTimeAvailable,
                            "for variant:",
                            this.currentVariantId
                          );
                        }
                        // Only update button state if real-time check confirms it's available
                        // Don't downgrade to unavailable if real-time check fails or returns false
                        // because we trust the cached data that says it's available
                        if (newRealTimeAvailable === true && this.currentVariantId === variantId) {
                          // Real-time check confirms availability - ensure button is still showing "Add to Bag"
                          const form = document.querySelector(
                            ".product-info .shopify-product-form"
                          );
                          const soldOut = document.querySelector(
                            ".product-info sold-out-badge"
                          );
                          if (form) {
                            this._updateButtonStateAfterColorChange(
                              form,
                              soldOut,
                              true,
                              variantForUpdate
                            );
                          }
                        } else if (!newRealTimeAvailable && this.debug) {
                          console.warn(
                            "[ProductGrouping] Real-time check shows variant unavailable, but cached data says available. Keeping 'Add to Bag' button based on cached data."
                          );
                        }
                      }
                    ).catch((error) => {
                      if (this.debug) {
                        console.warn(
                          "[ProductGrouping] Real-time availability check failed, using cached data:",
                          error
                        );
                      }
                      // On error, trust the cached data - don't change button state
                    });
                    
                    // Throw a special error to prevent the rest of the code from running
                    const switchError = new Error('Variant switched, button update handled');
                    switchError.isVariantSwitch = true;
                    throw switchError;
                  } else if (firstAvailableVariant) {
                    if (this.debug) {
                      console.log(
                        "[ProductGrouping] Preserved size unavailable, switching to first available variant:",
                        firstAvailableVariant.id,
                        "Available:",
                        firstAvailableVariant.available
                      );
                    }

                    // Switch to the first available variant
                    this.currentVariantId = firstAvailableVariant.id.toString();
                    
                    // Update variant input
                    const variantInput = document.querySelector(
                      '.product-info input[name="id"]'
                    );
                    if (variantInput) {
                      variantInput.value = this.currentVariantId;
                    }

                    // Update current options to match the new variant
                    if (firstAvailableVariant.option2) {
                      this.currentOptions.option2 = firstAvailableVariant.option2;
                    }
                    if (firstAvailableVariant.option3) {
                      this.currentOptions.option3 = firstAvailableVariant.option3;
                    }

                    // Update visual state of size/option buttons
                    this.updateVariantClass();

                    // Update URL with new variant
                    this._updateURLWithVariant(this.currentVariantId);

                    // Use cached availability (true) since we found a variant with available === true
                    // The real-time check might fail or be delayed, so trust the cached data
                    // Update button visibility immediately with the cached availability
                    // Use a small delay to ensure DOM is fully updated
                    setTimeout(() => {
                      // Re-query form and elements to ensure we have fresh references
                      const form = document.querySelector(
                        ".product-info .shopify-product-form"
                      );
                      const soldOut = document.querySelector(
                        ".product-info sold-out-badge"
                      );
                      if (form) {
                        this._updateButtonStateAfterColorChange(
                          form,
                          soldOut,
                          true, // Use cached availability - we know this variant is available
                          firstAvailableVariant
                        );
                      } else if (this.debug) {
                        console.warn(
                          "[ProductGrouping] Form not found when trying to update button state"
                        );
                      }
                    }, 100);
                    
                    // Optionally verify with real-time check in the background (but don't block on it)
                    // We trust the cached data since we found a variant with available === true
                    // Only update if real-time check confirms availability (don't downgrade to unavailable)
                    this._fetchVariantAvailability(this.currentVariantId).then(
                      (newRealTimeAvailable) => {
                        if (this.debug) {
                          console.log(
                            "[ProductGrouping] Real-time availability check result:",
                            newRealTimeAvailable,
                            "for variant:",
                            this.currentVariantId
                          );
                        }
                        // Only update button state if real-time check confirms it's available
                        // Don't downgrade to unavailable if real-time check fails or returns false
                        // because we trust the cached data that says it's available
                        if (newRealTimeAvailable === true && this.currentVariantId === firstAvailableVariant.id.toString()) {
                          // Real-time check confirms availability - ensure button is still showing "Add to Bag"
                          this._updateButtonStateAfterColorChange(
                            newAddToCartForm,
                            newSoldOutElement,
                            true,
                            firstAvailableVariant
                          );
                        } else if (!newRealTimeAvailable && this.debug) {
                          console.warn(
                            "[ProductGrouping] Real-time check shows variant unavailable, but cached data says available. Keeping 'Add to Bag' button based on cached data."
                          );
                        }
                      }
                    ).catch((error) => {
                      if (this.debug) {
                        console.warn(
                          "[ProductGrouping] Real-time availability check failed, using cached data:",
                          error
                        );
                      }
                      // On error, trust the cached data - don't change button state
                    });
                    
                    // Throw a special error to prevent the rest of the code from running
                    const switchError = new Error('Variant switched, button update handled');
                    switchError.isVariantSwitch = true;
                    throw switchError;
                  }
                }

                // If we didn't switch variants, check if we should show Join Waitlist
                // This happens when no available variant was found matching both waist and inseam
                if (!firstAvailableButton && !firstAvailableVariant && selectedWaist && selectedInseam) {
                  // No available variant found - explicitly show Join Waitlist
                  if (this.debug) {
                    console.log(
                      "[ProductGrouping] No available variant found - showing Join Waitlist for:",
                      { waist: selectedWaist, inseam: selectedInseam }
                    );
                  }
                  
                  setTimeout(() => {
                    const form = document.querySelector(".product-info .shopify-product-form");
                    const soldOut = document.querySelector(".product-info sold-out-badge");
                    if (form) {
                      // Get current variant for price display
                      const currentVariant = productData.product?.variants?.find(
                        v => v.id.toString() === this.currentVariantId?.toString()
                      );
                      
                      this._updateButtonStateAfterColorChange(
                        form,
                        soldOut,
                        false, // Not available
                        currentVariant || { id: this.currentVariantId }
                      );
                    }
                  }, 100);
                  
                  return; // Exit early, we've handled it
                }
                
                // If we didn't switch variants, proceed with normal button update
                // Re-query buttons after DOM update
                const buyButtonsEl =
                  newAddToCartForm?.querySelector("buy-buttons");
                const allButtons = buyButtonsEl
                  ? Array.from(buyButtonsEl.querySelectorAll("button"))
                  : [];

                const newNotifyMeBtn = allButtons.find((btn) =>
                  btn.hasAttribute("data-bis-trigger")
                );
                const newAddToCartButton = allButtons.find(
                  (btn) => !btn.hasAttribute("data-bis-trigger")
                );
                // Handle both button structures: with div wrapper (quick-add) and without (PDP)
                const newAddToCartButtonText =
                  newAddToCartButton?.querySelector("div") ||
                  newAddToCartButton;

                if (newAddToCartButton && newNotifyMeBtn) {
                  // Get the current variant to extract price for updateQuickAddCTA
                  const currentVariant = this._getCurrentVariant?.();
                  let variantPriceForCTA = null;
                  if (currentVariant && typeof currentVariant.price !== 'undefined') {
                    // Price is in cents, convert to dollars for display
                    const priceInCents = typeof currentVariant.price === 'string'
                      ? parseInt(currentVariant.price, 10)
                      : currentVariant.price;
                    if (!Number.isNaN(priceInCents) && priceInCents > 0) {
                      const dollars = priceInCents / 100;
                      variantPriceForCTA = dollars % 1 === 0 ? dollars.toString() : dollars.toFixed(2);
                    }
                  }
                  
                  this._updateButtonVisibility(
                    newAddToCartButton,
                    newNotifyMeBtn,
                    newAddToCartButtonText,
                    newSoldOutElement,
                    realTimeAvailable
                  );
                  
                  // Update quick add CTA with correct price
                  if (variantPriceForCTA) {
                    this.updateQuickAddCTA(realTimeAvailable, this.currentVariantId, variantPriceForCTA);
                  }

                  // CRITICAL: Notify AMP Back in Stock app about the new variant after color change
                  // This ensures the BIS modal shows the correct size options for the newly selected color
                  this._notifyAmpBackInStock({
                    id: this.currentVariantId,
                    available: realTimeAvailable,
                  });

                  // Dispatch variant:change event for other systems
                  // Get variant price for the event
                  const currentVariantForEvent = this._getCurrentVariant?.();
                  let variantPriceForEvent = null;
                  if (currentVariantForEvent && typeof currentVariantForEvent.price !== 'undefined') {
                    variantPriceForEvent = currentVariantForEvent.price;
                  } else {
                    // Try to get price from active button
                    const activeButton = document.querySelector('button[js-update-variant].is-active, button[js-update-variant][aria-checked="true"]');
                    if (activeButton && activeButton.dataset.variantPrice) {
                      const priceStr = activeButton.dataset.variantPrice;
                      const priceValue = parseFloat(priceStr);
                      if (!Number.isNaN(priceValue)) {
                        variantPriceForEvent = Math.round(priceValue * 100); // Convert to cents
                      }
                    }
                  }
                  
                  document.dispatchEvent(
                    new CustomEvent("variant:change", {
                      bubbles: true,
                      detail: {
                        variant: {
                          id: this.currentVariantId,
                          available: realTimeAvailable,
                          price: variantPriceForEvent,
                        },
                        formId: newAddToCartForm?.id,
                      },
                    })
                  );
                } else {
                  console.warn(
                    "[ProductGrouping] Buttons not found after color change, retrying...",
                    {
                      hasAddToCart: !!newAddToCartButton,
                      hasWaitlist: !!newNotifyMeBtn,
                      totalButtons: allButtons.length,
                    }
                  );
                }
              })
              .catch((error) => {
                // Ignore the special error that indicates we've already handled the button update
                if (error.isVariantSwitch) {
                  if (this.debug) {
                    console.log(
                      "[ProductGrouping] Variant switched, button update already handled"
                    );
                  }
                  return;
                }
                console.error(
                  "[ProductGrouping] Failed to fetch availability after color change:",
                  error
                );
              });
          }
        });

        const scripts = Array.from(
          newMainProduct?.querySelectorAll("script") || []
        ).filter((script) => !script.hasAttribute("data-product-group"));
        scripts.forEach((oldScript) => {
          const newScript = document.createElement("script");
          newScript.textContent = oldScript.textContent;
          document.body.appendChild(newScript);
          document.body.removeChild(newScript);
        });

        window.dispatchEvent(new Event("productGroupingInjected"));

        if (!this.isQuickView) {
          params.delete("view");
          params.delete("section_id");
          params.delete("no-fade-in");
          if (loadedIds) {
            params.delete("product-group-ids");
          }
          url.search = params.toString();
          window.history.replaceState({}, "", url.toString());

          const optionColor = document.querySelector(
            `button[data-product-handle="${newProduct}"]`
          );
          const currentQuickAdd = document.querySelector("#quick-add--sticky");
          const newQuickAdd = newDocument.querySelector("#quick-add--sticky");
          if (currentQuickAdd && newQuickAdd) {
            currentQuickAdd.replaceWith(newQuickAdd);
          }

          setTimeout(() => {
            if (!this.isQuickView) {
              window.yotpoWidgetsContainer?.initWidgets();
            }
            if (optionColor) {
              optionColor.focus();
            }
            const isMobile = window.matchMedia("(max-width: 768px)").matches;
            if (isMobile) {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }, 300);
        }

        this.currentProductHandle = newProduct;
      } catch (error) {
        console.error(
          "[ProductGrouping] Error fetching new product data:",
          error
        );

        // User-friendly fallback: reload the page with the correct product
        if (this.debug) {
          console.log(
            "[ProductGrouping] Attempting full page navigation to:",
            newProduct
          );
        }
        // Only navigate if we have a valid product handle
        if (newProduct && typeof newProduct === "string") {
          window.location.href = `/products/${newProduct}`;
        }
      } finally {
        this.parentComponent.classList.remove('is-loading');
      }
    }
  }

  _setupSizeGuide() {
    this.sizeGuideButton = document.querySelector(".open-size-guide");
    this.sizeGuideModal = document.querySelector(".size-guide-modal");
    this.closeButton = document.querySelector(".modal-close");
    this.modalContent = document.querySelector(".modal-content");

    if (this.sizeGuideButton) {
      this.sizeGuideButton.addEventListener("click", () => this.openSizeGuideModal());
    }

    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => this.closeSizeGuideModal());
    }

    if (this.sizeGuideModal) {
      this.sizeGuideModal.addEventListener("click", (event) => {
        if (event.target === this.sizeGuideModal) {
          this.closeSizeGuideModal();
        }
      });
    }

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.sizeGuideModal?.classList.contains("active")) {
        this.closeSizeGuideModal();
      }
    });

    this.tabButtons = document.querySelectorAll(".size-guide-tab-buttons .tab-button");
    this.tabPanels = document.querySelectorAll(".size-guide-tab-content .tab-panel");

    this.tabButtons.forEach((button) => {
      button.addEventListener("click", (event) => this.switchTab(event));
    });

    window.addEventListener("productGroupingInjected", () => this._updateSizeGuide());
  }

  switchTab(event) {
    const selectedTab = event.target.getAttribute("data-tab");
    this.tabButtons.forEach((btn) => btn.classList.remove("active"));
    this.tabPanels.forEach((panel) => panel.classList.remove("active"));
    event.target.classList.add("active");
    document.getElementById(selectedTab)?.classList.add("active");
  }

  openSizeGuideModal() {
    this.sizeGuideModal?.classList.add("active");
    document.body.classList.add('modal-open');
    this._generateSizeChartTabs();
  }

  closeSizeGuideModal() {
    this.sizeGuideModal?.classList.remove('active');
    document.body.classList.remove('modal-open');
  }

  _generateSizeChartTabs() {
    const sizeChartContent = document.querySelector('#size-chart-content');
    if (!sizeChartContent) return;

    const tables = sizeChartContent.querySelectorAll("table");
    const paragraphs = sizeChartContent.querySelectorAll("p");

    if (tables.length === 0) return;

    let subTabsHTML = `<div class="size-guide-sub-tabs" role="tablist">`;
    let subContentHTML = `<div class="size-guide-sub-content">`;

    tables.forEach((table, index) => {
      let label = `Size Chart ${index + 1}`;
      for (let i = paragraphs.length - 1; i >= 0; i--) {
        if (paragraphs[i].nextElementSibling === table && paragraphs[i].textContent.trim() !== "") {
          label = paragraphs[i].textContent.trim();
          break;
        }
      }
      subTabsHTML += `<button class="sub-tab-button ${index === 0 ? 'active' : ''}" data-tab="sub-tab-${index}" role="tab" aria-controls="sub-tab-${index}" aria-selected="${index === 0 ? 'true' : 'false'}">${label}</button>`;
      subContentHTML += `<div class="sub-tab-panel ${index === 0 ? 'active' : ''}" id="sub-tab-${index}" role="tabpanel">${table.outerHTML}</div>`;
    });

    subTabsHTML += `</div>`;
    subContentHTML += `</div>`;

    sizeChartContent.innerHTML = subTabsHTML + subContentHTML;

    document.querySelectorAll(".sub-tab-button").forEach(button => {
      button.addEventListener("click", function() {
        const tab = this.getAttribute("data-tab");
        document.querySelectorAll(".sub-tab-button").forEach(btn => btn.classList.remove("active"));
        document.querySelectorAll(".sub-tab-panel").forEach(panel => panel.classList.remove("active"));
        this.classList.add("active");
        document.getElementById(tab)?.classList.add("active");
      });
    });
  }

  _updateSizeGuide() {
    const productTags = this.getAttribute("data-product-tags") || "";
    const sizeGuideHandle = this._extractSizeGuideTag(productTags);

    if (sizeGuideHandle) {
      fetch(`/pages/${sizeGuideHandle}`)
        .then(response => response.text())
        .then(sizeChart => {
          document.querySelector("#size-chart-content").innerHTML = sizeChart;
        })
        .catch(error => console.error("Error loading size guide:", error));
    }
  }

  _extractSizeGuideTag(tags) {
    const match = tags.split(",").find(tag => tag.includes("size-guide:"));
    return match ? match.split(":")[1] : "";
  }

  /**
   * Get the currently selected size from active size buttons
   */
  _getCurrentSelectedSize() {
    // Look for active size buttons in the product grouping component
    const activeSizeButton = this.querySelector('.product-grouping__options.is-size .product-option.is-active');
    if (activeSizeButton) {
      const sizeText = activeSizeButton.querySelector('span')?.textContent?.trim();
      if (this.debug) console.log('Found active size button:', sizeText);
      return sizeText;
    }

    // Fallback: check variant picker size buttons  
    const activeVariantButton = this.querySelector('.variant-picker__option-values .product-option.is-active');
    if (activeVariantButton) {
      const sizeText = activeVariantButton.querySelector('span')?.textContent?.trim();
      if (this.debug) console.log('Found active variant button:', sizeText);
      return sizeText;
    }

    // Last resort: get from current variant data
    const currentVariant = this._getCurrentVariant();
    if (currentVariant) {
      // Size could be in option2 or option3 depending on product structure
      const possibleSize = currentVariant.option2 || currentVariant.option3;
      if (possibleSize && possibleSize !== 'Default Title') {
        if (this.debug) console.log('Found size from current variant:', possibleSize);
        return possibleSize;
      }
    }

    if (this.debug) console.log('No current size found');
    return null;
  }

  /**
   * Get current variant data from the variant picker or product grouping
   */
  _getCurrentVariant() {
    try {
      // First, try variant-picker (for standard products)
      const variantScript = document.querySelector('variant-picker script[data-variant]');
      if (variantScript) {
        const variant = JSON.parse(variantScript.textContent || '{}');
        if (variant && variant.id) {
          if (this.debug) console.log('Found variant from variant-picker:', variant);
          return variant;
        }
      }

      // Fallback: For product-grouping, reconstruct from current state
      if (this.currentVariantId && this.productGroupingData && this.currentProductId) {
        const productData = this.productGroupingData[this.currentProductId];
        if (productData && productData.variants) {
          const currentVariant = productData.variants.find(
            v => v.id.toString() === this.currentVariantId.toString()
          );
          if (currentVariant) {
            if (this.debug) console.log('Found variant from product-grouping data:', currentVariant);
            return currentVariant;
          }
        }
      }

      // Last resort: Use tracked options to build a partial variant object
      if (this.currentOptions.option2 || this.currentOptions.option3) {
        const partialVariant = {
          id: this.currentVariantId,
          option2: this.currentOptions.option2,
          option3: this.currentOptions.option3
        };
        
        // Try to get price from product grouping data
        if (this.currentVariantId && this.productGroupingData && this.currentProductId) {
          const productData = this.productGroupingData[this.currentProductId];
          if (productData && productData.variants) {
            const variantFromData = productData.variants.find(
              v => v.id.toString() === this.currentVariantId.toString()
            );
            if (variantFromData && variantFromData.price) {
              partialVariant.price = variantFromData.price;
              if (this.debug) {
                console.log('Got price from product grouping data for partial variant:', partialVariant.price);
              }
            }
          }
        }
        
        // If still no price, try to get from active button
        if (!partialVariant.price) {
          const activeButton = document.querySelector('button[js-update-variant].is-active, button[js-update-variant][aria-checked="true"]');
          if (activeButton && activeButton.dataset.variantPrice) {
            const priceStr = activeButton.dataset.variantPrice;
            const priceValue = parseFloat(priceStr);
            if (!Number.isNaN(priceValue)) {
              // Price is in dollars, convert to cents
              partialVariant.price = Math.round(priceValue * 100);
              if (this.debug) {
                console.log('Got price from active button for partial variant:', partialVariant.price);
              }
            }
          }
        }
        
        // If still no price, try variant picker script
        if (!partialVariant.price) {
          const variantScript = document.querySelector('variant-picker script[data-variant]');
          if (variantScript) {
            try {
              const variantFromScript = JSON.parse(variantScript.textContent || '{}');
              if (variantFromScript && variantFromScript.id && variantFromScript.id.toString() === this.currentVariantId.toString() && variantFromScript.price) {
                partialVariant.price = variantFromScript.price;
                if (this.debug) {
                  console.log('Got price from variant picker script for partial variant:', partialVariant.price);
                }
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
        }
        
        if (this.debug) console.log('Built variant from currentOptions:', partialVariant);
        return partialVariant;
      }
    } catch (error) {
      if (this.debug) console.warn('Could not parse current variant:', error);
    }

    if (this.debug) console.warn('No current variant found from any source');
    return null;
  }

  /**
   * Find a variant that matches the target option2 and/or option3 values
   * Works for both regular products (single size) and pants (waist + inseam)
   */
  _findMatchingVariant(variants, targetOption2, targetOption3) {
    if (!variants || (!targetOption2 && !targetOption3)) {
      if (this.debug) console.log('_findMatchingVariant: No variants or target options provided');
      return null;
    }

    // Normalize "Default Title" to null for easier comparison
    const normalizedOption2 = targetOption2 === 'Default Title' ? null : targetOption2;
    const normalizedOption3 = targetOption3 === 'Default Title' ? null : targetOption3;

    if (this.debug) {
      console.log('_findMatchingVariant searching for:', {
        option2: normalizedOption2,
        option3: normalizedOption3,
        totalVariants: variants.length
      });
      // Log a sample of variants to see their structure
      console.log('Sample variants:', variants.slice(0, 3).map(v => ({
        id: v.id,
        option1: v.option1,
        option2: v.option2,
        option3: v.option3
      })));
    }

    try {
      for (const variant of variants) {
        // Match both option2 and option3 if both are meaningful values
        if (normalizedOption2 && normalizedOption3) {
          // Normalize option values by removing inch symbols and whitespace for comparison
          // This handles cases where one product uses '32' and another uses '32"'
          const normalizeValue = (val) => val ? val.toString().replace(/["'"\s]/g, '') : '';
          
          const variantOption2Normalized = normalizeValue(variant.option2);
          const targetOption2Normalized = normalizeValue(normalizedOption2);
          const variantOption3Normalized = normalizeValue(variant.option3);
          const targetOption3Normalized = normalizeValue(normalizedOption3);
          
          const option2Match = variantOption2Normalized === targetOption2Normalized;
          const option3Match = variantOption3Normalized === targetOption3Normalized;
          
          if (this.debug && (option2Match || option3Match)) {
            console.log('Checking variant:', variant.id, {
              variantOption2: variant.option2,
              variantOption2Normalized,
              targetOption2: normalizedOption2,
              targetOption2Normalized,
              option2Match,
              variantOption3: variant.option3,
              variantOption3Normalized,
              targetOption3: normalizedOption3,
              targetOption3Normalized,
              option3Match
            });
          }
          
          if (option2Match && option3Match) {
            if (this.debug) console.log('✓ Found exact match (option2 + option3):', variant.id, variant);
            return variant.id.toString();
          }
        }
        // Match just option2 if option3 is not meaningful
        else if (normalizedOption2 && !normalizedOption3) {
          if (variant.option2 === normalizedOption2) {
            if (this.debug) console.log('✓ Found option2 match:', variant.id, variant);
            return variant.id.toString();
          }
        }
      }
    } catch (error) {
      if (this.debug) console.warn('Error finding matching variant:', error);
    }

    if (this.debug) console.log('No matching variant found for the target options');
    return null;
  }

  /**
   * Check if an option represents a size selection
   */
  _isOptionSize(optionIndex, value) {
    // Look for size-related indicators
    const sizeIndicators = [
      "xs",
      "xsmall",
      "s",
      "small",
      "m",
      "medium",
      "l",
      "large",
      "xl",
      "xlarge",
      "xxl",
      "2xl",
      "3xl",
      "28",
      "29",
      "30",
      "31",
      "32",
      "33",
      "34",
      "35",
      "36",
      "38",
      "40",
      "42",
      "44",
      "46",
      "48",
      "50",
    ];

    const valueLower = value.toLowerCase().replace(/[^a-z0-9]/g, "");

    // Check if the value looks like a size
    const looksLikeSize = sizeIndicators.some((indicator) =>
      valueLower.includes(indicator)
    );

    // Also check if this option row is marked as size
    const optionRow = this.querySelector(`[data-options-row="${optionIndex}"]`);
    const isMarkedAsSize = optionRow?.classList.contains("is-size");

    return looksLikeSize || isMarkedAsSize;
  }

  /**
   * Get the size that should be preserved during color changes
   * Prioritizes user's preferred size if they've made a selection
   */
  _getSizeToPreserve() {
    // If user has explicitly selected a size, always try to preserve their preference
    if (this.hasUserSelectedSize && this.preferredSize) {
      return this.preferredSize;
    }

    // Otherwise fall back to current selection
    const currentSize = this._getCurrentSelectedSize();

    // If we found a current size and haven't captured a preference yet, capture it
    if (currentSize && !this.hasUserSelectedSize) {
      this.preferredSize = currentSize;
      this.hasUserSelectedSize = true;
    }
    
    return currentSize;
  }

  /**
   * Switch to a specific variant (programmatically)
   */
  _switchToVariant(variant) {
    if (!variant || !variant.button) {
      console.error('Invalid variant data for switching:', variant);
      return;
    }

    // Simulate a click on the variant button to trigger the normal flow
    const clickEvent = new Event('click', { bubbles: true, cancelable: true });
    variant.button.dispatchEvent(clickEvent);
  }

  /**
   * Helper method to update button state after color change
   */
  _updateButtonStateAfterColorChange(
    addToCartForm,
    soldOutElement,
    isAvailable,
    variant
  ) {
    if (this.debug) {
      console.log(
        "[ProductGrouping] _updateButtonStateAfterColorChange called:",
        {
          hasForm: !!addToCartForm,
          isAvailable,
          variantId: variant?.id,
          variantAvailable: variant?.available
        }
      );
    }
    
    if (!addToCartForm) {
      if (this.debug) {
        console.warn("[ProductGrouping] No addToCartForm found");
      }
      return;
    }

    const buyButtonsEl = addToCartForm.querySelector("buy-buttons");
    const allButtons = buyButtonsEl
      ? Array.from(buyButtonsEl.querySelectorAll("button"))
      : [];

    const notifyMeBtn = allButtons.find((btn) =>
      btn.hasAttribute("data-bis-trigger")
    );
    const addToCartButton = allButtons.find(
      (btn) => !btn.hasAttribute("data-bis-trigger")
    );
    const addToCartButtonText =
      addToCartButton?.querySelector("div") || addToCartButton;

    if (this.debug) {
      console.log(
        "[ProductGrouping] Found buttons:",
        {
          hasAddToCartButton: !!addToCartButton,
          hasNotifyMeBtn: !!notifyMeBtn,
          hasButtonText: !!addToCartButtonText,
          totalButtons: allButtons.length
        }
      );
    }

    if (addToCartButton && notifyMeBtn) {
      // Price will be read directly from sale-price element in _updateButtonVisibility
      // No need to calculate it here - just pass variant for availability check
      const variantForPrice = {
        id: variant.id,
        price: typeof variant.price === 'string'
          ? parseInt(variant.price, 10)
          : variant.price
      };

      // When color changes, the HTML is replaced, so we need to wait for the new price-list to be ready
      // Use requestAnimationFrame to ensure DOM is updated, then try multiple times if needed
      const updateButton = () => {
        this._updateButtonVisibility(
          addToCartButton,
          notifyMeBtn,
          addToCartButtonText,
          soldOutElement,
          isAvailable,
          variantForPrice
        );
        
        // Check if button text was updated with a price
        const buttonText = addToCartButtonText?.textContent || '';
        const hasPrice = buttonText.includes('$') && /\$\d/.test(buttonText);
        
        // If no price in button text, retry after a short delay (price-list might still be updating)
        if (!hasPrice && isAvailable) {
          setTimeout(() => {
            this._updateButtonVisibility(
              addToCartButton,
              notifyMeBtn,
              addToCartButtonText,
              soldOutElement,
              isAvailable,
              variantForPrice
            );
          }, 150);
        }
      };
      
      // Wait for DOM to update after color change (double RAF to ensure all updates are done)
      requestAnimationFrame(() => {
        requestAnimationFrame(updateButton);
      });

      // Update quick add CTA
      if (variant.price) {
        const priceInCents = typeof variant.price === 'string'
          ? parseInt(variant.price, 10)
          : variant.price;
        if (!Number.isNaN(priceInCents) && priceInCents > 0) {
          const dollars = priceInCents / 100;
          const variantPriceForCTA = dollars % 1 === 0 ? dollars.toString() : dollars.toFixed(2);
          this.updateQuickAddCTA(isAvailable, variant.id.toString(), variantPriceForCTA);
        }
      }

      // Notify AMP Back in Stock app
      this._notifyAmpBackInStock({
        id: variant.id.toString(),
        available: isAvailable,
        price: variant.price,
      });

      // Dispatch variant:change event
      document.dispatchEvent(
        new CustomEvent("variant:change", {
          bubbles: true,
          detail: {
            variant: {
              id: variant.id.toString(),
              available: isAvailable,
              price: variant.price,
            },
            formId: addToCartForm?.id,
          },
        })
      );
    }
  }

  /**
   * CRITICAL SAFETY NET: Monitor button state and prevent unwanted hiding
   * This acts as a final safeguard against any code that might hide the Add to Cart button
   * OPTIMIZED: Uses MutationObserver instead of polling for better performance
   */
  _setupButtonStateGuardian() {
    // Wait for DOM to be fully ready
    setTimeout(() => {
      const addToCartForm = document.querySelector(".product-info .shopify-product-form");
      if (!addToCartForm) return;

      const buyButtonsEl = addToCartForm.querySelector("buy-buttons");
      if (!buyButtonsEl) return;

      let isVerifying = false; // Prevent concurrent verifications

      const verifyButtonState = () => {
        // Skip if already verifying or no variant selected
        if (isVerifying || !this.currentVariantId) return;

        const allButtons = Array.from(buyButtonsEl.querySelectorAll("button"));
        const notifyMeBtn = allButtons.find((btn) =>
          btn.hasAttribute("data-bis-trigger")
        );
        const addToCartButton = allButtons.find(
          (btn) => !btn.hasAttribute("data-bis-trigger") && btn.name === "add"
        );

        if (!addToCartButton || !notifyMeBtn) return;

        // Check for suspicious button states
        const isAddToCartHidden =
          addToCartButton.style.display === "none" ||
          addToCartButton.style.visibility === "hidden";
        const isWaitlistHidden = notifyMeBtn.style.display === "none";

        // If both are hidden, that's definitely wrong
        if (isAddToCartHidden && isWaitlistHidden) {
          if (this.debug) {
            console.warn(
              "[ProductGrouping] Both buttons hidden - correcting state"
            );
          }

          isVerifying = true;
          this._fetchVariantAvailability(this.currentVariantId)
            .then((isAvailable) => {
              const soldOutElement = document.querySelector(
                ".product-info sold-out-badge"
              );
              const buttonText = addToCartButton.querySelector("div");

              this._updateButtonVisibility(
                addToCartButton,
                notifyMeBtn,
                buttonText,
                soldOutElement,
                isAvailable
              );

              if (this.debug) {
                console.log(
                  `[ProductGrouping] Button state corrected to: ${
                    isAvailable ? "Add to Bag" : "Waitlist"
                  }`
                );
              }
            })
            .catch((error) => {
              console.error(
                "[ProductGrouping] Guardian failed to verify availability:",
                error
              );
            })
            .finally(() => {
              isVerifying = false;
            });
        }
      };

      // Use MutationObserver to watch for style changes (much more efficient than polling)
      const observer = new MutationObserver((mutations) => {
        // Only verify if style attributes changed
        const styleChanged = mutations.some(
          (m) => m.type === "attributes" && m.attributeName === "style"
        );
        if (styleChanged) {
          verifyButtonState();
        }
      });

      // Observe both buttons for style changes
      const allButtons = Array.from(buyButtonsEl.querySelectorAll("button"));
      allButtons.forEach((btn) => {
        observer.observe(btn, { attributes: true, attributeFilter: ["style"] });
      });

      // Initial check
      verifyButtonState();

      // Store cleanup function
      this._guardianCleanup = () => {
        observer.disconnect();
      };
    }, 100);
  }
}

customElements.define('product-grouping', ProductGrouping);